import * as _ from 'lodash-es';
import React, { useMemo, useState } from 'react';
import ToolbarButton, { ToolbarButtonGroup } from './buttons/ToolbarButton';
import ToolbarCommentButtonGroup from './buttons/ToolbarCommentButtonGroup';
import ToolbarSpanButtonGroup from './buttons/ToolbarSpanButtonGroup';
import ToolbarSyntaxButton from './buttons/ToolbarSyntaxButton';
import FloatingToolbar from './FloatingToolbar';
import formatBlotWithoutScrolling from './formatBlotWithoutScrolling';
import Toolbar from './Toolbar';
import { useAppDispatch } from '../../../app/redux/useAppSelector';
import readIcon from '../../../icons/non-prefilled/16x16/read.svg';
import { UIActions } from '../../../ui/uiSlice';
import type CodeEmbed from '../../formats/CodeEmbed';
import type { HoveredBlotListenerSetting } from '../../HoveredBlotListener';
import { useHoveredBlotListener } from '../../HoveredBlotListener';
import { useQuill, useQuillReadOnly } from '../../QuillContext';
import type QuillEditor from '../../QuillEditor';
import { codeIcon, copyIcon } from '../editorIcons';

export enum PreviewStatus {
  Active = 'Active',
  Available = 'Available',
  NotAvailable = 'NotAvailable',
  Errored = 'Errored',
}

type Formats = { language?: string | null; comment?: string[]; span?: string };

interface BaseCodeEmbedToolbarProps {
  quill: QuillEditor;
  hoveredBlot?: CodeEmbed;
  status: PreviewStatus;
  formats?: Formats;
  setIsPreviewing: (isPreviewing: boolean) => void;
  setPersist: (persist: boolean) => void;
}

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const BaseCodeEmbedToolbar = ({
  quill,
  hoveredBlot,
  status,
  formats,
  setIsPreviewing,
  setPersist,
}: BaseCodeEmbedToolbarProps) => {
  const readOnly = useQuillReadOnly();
  const dispatch = useAppDispatch();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const format = (attributes: Record<string, unknown>) => {
    if (!hoveredBlot) return;
    formatBlotWithoutScrolling({ quill, blot: hoveredBlot, attributes });
  };

  const buttons = [];

  const isShowingPreview =
    status === PreviewStatus.Available || status === PreviewStatus.Errored;

  if (isShowingPreview) {
    const tooltip = (() => {
      switch (formats?.language) {
        case 'latex':
          return 'Preview rendered equation';
        case 'mermaid':
          return 'Preview rendered chart';
        default:
          return '';
      }
    })();

    buttons.push(
      <ToolbarButtonGroup key="preview">
        <ToolbarButton
          icon={readIcon}
          tooltip={tooltip}
          disabled={status === PreviewStatus.Errored}
          onClick={() => setIsPreviewing(true)}
          testId="codeEmbedPreviewButton"
        >
          Preview
        </ToolbarButton>
      </ToolbarButtonGroup>,
    );
  } else if (status === PreviewStatus.Active) {
    buttons.push(
      <ToolbarButtonGroup key="show code">
        <ToolbarButton
          tooltip="See underlying code"
          icon={codeIcon}
          onClick={() => {
            setIsPreviewing(false);
          }}
          aria-label="Show code"
        >
          Code
        </ToolbarButton>
      </ToolbarButtonGroup>,
    );
  }

  if (status !== PreviewStatus.Active) {
    const isLabelHidden =
      (readOnly && isShowingPreview) || (!readOnly && !isShowingPreview);
    buttons.push(
      <ToolbarButtonGroup key="copy">
        <ToolbarButton
          tooltip="Copy code"
          icon={copyIcon}
          onClick={async () => {
            if (!hoveredBlot) return;

            await navigator.clipboard.writeText(
              _.trim(hoveredBlot.code(), '\n'),
            );

            dispatch(
              UIActions.pushToast({
                level: 'success',
                message: 'Code copied',
              }),
            );
          }}
          testId="codeEmbedCopyButton"
        >
          {isLabelHidden ? '' : 'Copy'}
        </ToolbarButton>
      </ToolbarButtonGroup>,
    );
  }

  if (!readOnly) {
    buttons.push(
      <ToolbarSpanButtonGroup
        key="span"
        span={formats?.span}
        onSpanChange={(newSpan) => format({ span: newSpan })}
      />,
    );
  }

  if (status !== PreviewStatus.Active && !readOnly) {
    buttons.push(
      <ToolbarButtonGroup key="syntax">
        <ToolbarSyntaxButton
          languageKey={formats?.language}
          dropdownOpen={isDropdownOpen}
          onDropdownOpenChange={(open) => {
            setIsDropdownOpen(open);
            setPersist(open);
          }}
          onSelect={(newLanguageKey) => {
            dispatch(UIActions.changeCodeBlockSyntax(newLanguageKey));
            if (hoveredBlot) {
              formatBlotWithoutScrolling({
                quill,
                blot: hoveredBlot,
                attributes: { language: newLanguageKey },
              });
            }
          }}
        />
      </ToolbarButtonGroup>,
    );
  }

  if (status === PreviewStatus.Active) {
    buttons.push(
      <ToolbarCommentButtonGroup
        key="comment"
        blot={hoveredBlot}
        existingComment={formats?.comment}
        onReactionPickerOpenChange={setPersist}
      />,
    );
  }

  return (
    <Toolbar
      onEditButtonClick={() => {
        hoveredBlot?.setIsPreviewing(false);
      }}
    >
      {buttons}
    </Toolbar>
  );
};

interface CodeBlockToolbarProps {
  blot: CodeEmbed;
  status: PreviewStatus;
  setIsPreviewing: (isPreviewing: boolean) => void;
}

const CodeEmbedToolbar = ({
  blot,
  status,
  setIsPreviewing,
}: CodeBlockToolbarProps) => {
  const { quill } = useQuill();

  const hoveredBlotListenerSetting: HoveredBlotListenerSetting<
    Formats,
    CodeEmbed
  > = useMemo(
    () => ({
      isHoveredBlot(hoveredBlot) {
        return hoveredBlot === blot;
      },
      getContext(hoveredBlot) {
        return hoveredBlot.formats();
      },
    }),
    [blot],
  );

  const {
    bounds,
    hoveredBlot,
    context: formats,
    setPersist,
  } = useHoveredBlotListener(quill, hoveredBlotListenerSetting);

  if (!quill) return null;

  return (
    <FloatingToolbar.Toolbar bounds={bounds}>
      <BaseCodeEmbedToolbar
        hoveredBlot={hoveredBlot}
        status={status}
        setIsPreviewing={setIsPreviewing}
        setPersist={setPersist}
        formats={formats}
        quill={quill}
      />
    </FloatingToolbar.Toolbar>
  );
};

export default CodeEmbedToolbar;
